import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { fetchEmployer, api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Users, ShieldCheck, AlertTriangle, XCircle, Bell, ArrowUpRight, Filter, Clock, HardHat, ChevronDown } from "lucide-react";

const EMPLOYER_ID = "northwest-electrical";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "cleared", label: "Ready for Site" },
  { key: "review", label: "Action Required" },
  { key: "not_cleared", label: "Not Eligible" },
];

const severityStyle = {
  high: "border-l-red-500 bg-red-50 text-red-900",
  medium: "border-l-amber-500 bg-amber-50 text-amber-900",
  low: "border-l-slate-400 bg-slate-50 text-slate-800",
};

export default function EmployerDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null); // worker id
  const [detailsByWorker, setDetailsByWorker] = useState({}); // { workerId: [creds] }

  useEffect(() => {
    fetchEmployer(EMPLOYER_ID)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleExpand = async (workerId) => {
    if (expanded === workerId) {
      setExpanded(null);
      return;
    }
    setExpanded(workerId);
    if (!detailsByWorker[workerId]) {
      try {
        const res = await api.get(`/workers/${workerId}`);
        setDetailsByWorker((s) => ({ ...s, [workerId]: res.data.credentials || [] }));
      } catch {
        setDetailsByWorker((s) => ({ ...s, [workerId]: [] }));
      }
    }
  };

  const filteredWorkers = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.workers;
    return data.workers.filter((w) => w.clearance === filter);
  }, [data, filter]);

  if (loading || !data) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-20 text-center text-slate-500" data-testid="dashboard-loading">
        Loading compliance dashboard…
      </div>
    );
  }

  const metrics = [
    {
      key: "assigned",
      label: "Workers Assigned to Site",
      value: data.metrics.workers_assigned_to_site ?? data.metrics.total_workers,
      icon: HardHat,
      accent: "text-[#0B1221]",
      bg: "bg-white",
      sublabel: "workers",
    },
    {
      key: "ready",
      label: "Workers Ready to Work Today",
      value: data.metrics.ready_to_work_today ?? 0,
      icon: ShieldCheck,
      accent: "text-emerald-600",
      bg: "bg-white",
      sublabel: "identity + compliance",
    },
    {
      key: "expiring",
      label: "Credentials Expiring in 30 Days",
      value: data.metrics.credentials_expiring_30 ?? 0,
      icon: Clock,
      accent: "text-amber-600",
      bg: "bg-white",
      sublabel: "renewals due",
    },
    {
      key: "not_cleared",
      label: "Not Eligible",
      value: data.metrics.not_cleared,
      icon: XCircle,
      accent: "text-red-600",
      bg: "bg-white",
      sublabel: "blocked from site",
    },
  ];

  return (
    <div data-testid="employer-dashboard-page" className="min-h-[calc(100vh-65px)] bg-[#F8FAFC]">
      <div className="mx-auto max-w-7xl px-6 py-10 md:px-12 md:py-14">
        {/* Header */}
        <div className="flex flex-col items-start justify-between gap-4 border-b border-slate-200 pb-6 md:flex-row md:items-end">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
              03 / Employer · Compliance Dashboard
            </div>
            <h1
              data-testid="dashboard-employer-name"
              className="font-display mt-2 text-3xl font-bold tracking-tight text-[#0B1221] sm:text-4xl"
            >
              {data.employer.name}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-600">
              <span><strong className="text-[#0B1221]">Primary site:</strong> {data.employer.primary_job_site}</span>
              <span><strong className="text-[#0B1221]">Workforce:</strong> {data.employer.workforce_size} workers</span>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
            <span className="h-1.5 w-1.5 animate-pulse bg-emerald-500" />
            Live · syncing every 30s
          </div>
        </div>

        {/* Metric cards */}
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          {metrics.map((m) => (
            <div
              key={m.key}
              data-testid={`metric-${m.key}`}
              className={`group relative border border-slate-200 ${m.bg} p-5 transition-shadow hover:shadow-md`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 leading-snug min-h-[28px]">
                  {m.label}
                </div>
                <m.icon className={`h-4 w-4 shrink-0 ${m.accent}`} strokeWidth={2} />
              </div>
              <div className={`font-display mt-3 text-4xl font-black tracking-tight ${m.accent}`}>
                {m.value}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">{m.sublabel ?? "workers"}</div>
            </div>
          ))}
        </div>

        {/* Main grid: table + alerts */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Worker table */}
          <div className="lg:col-span-2">
            <div className="border border-slate-200 bg-white">
              <div className="flex flex-col items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-center">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                    Workforce readiness
                  </div>
                  <div className="font-display mt-0.5 text-lg font-bold text-[#0B1221]">
                    Who can work today
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Filter className="mr-1 h-3.5 w-3.5 text-slate-400" />
                  {FILTERS.map((f) => (
                    <button
                      key={f.key}
                      data-testid={`filter-${f.key}`}
                      onClick={() => setFilter(f.key)}
                      className={`border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                        filter === f.key
                          ? "border-[#0B1221] bg-[#0B1221] text-white"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="workers-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="px-5 py-3 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Worker</th>
                      <th className="px-5 py-3 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Role</th>
                      <th className="px-5 py-3 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Job Site</th>
                      <th className="px-5 py-3 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Status</th>
                      <th className="px-5 py-3 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Detail</th>
                      <th className="px-5 py-3 text-right font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWorkers.map((w, idx) => (
                      <Fragment key={w.id}>
                      <tr
                        data-testid={`worker-row-${idx}`}
                        className="border-b border-slate-100 transition-colors hover:bg-slate-50"
                      >
                        <td className="px-5 py-4 align-middle">
                          <div className="flex items-center gap-3">
                            <div
                              data-testid={`worker-row-${idx}-identity-photo`}
                              className="relative h-10 w-10 shrink-0 overflow-hidden border border-slate-200 bg-slate-100"
                              aria-label={`${w.name} identity photo`}
                            >
                              {w.photo_url ? (
                                <img
                                  src={w.photo_url}
                                  alt={`${w.name} identity photo`}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center font-display text-[10px] font-black text-slate-400">
                                  {w.name.split(" ").map((n) => n[0]).join("")}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-display font-bold text-[#0B1221]">{w.name}</span>
                              {w.identity_verified && (
                                <span
                                  data-testid={`worker-row-${idx}-identity-badge`}
                                  className="inline-flex items-center gap-1 whitespace-nowrap border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700"
                                >
                                  <ShieldCheck className="h-2.5 w-2.5" strokeWidth={2.5} />
                                  Identity Verified
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 align-middle text-slate-700">{w.trade}</td>
                        <td className="px-5 py-4 align-middle text-slate-700">{w.job_site}</td>
                        <td className="px-5 py-4 align-middle">
                          <StatusBadge status={w.clearance} testid={`worker-row-${idx}-status`} />
                        </td>
                        <td className="px-5 py-4 align-middle text-slate-600">
                          {w.clearance === "cleared" ? (
                            <span className="text-emerald-700">Ready to deploy</span>
                          ) : (
                            w.reason
                          )}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            data-testid={`worker-row-${idx}-view`}
                            onClick={() => toggleExpand(w.id)}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-[#FF5A1F] hover:underline"
                          >
                            {expanded === w.id ? "Hide" : "View Details"}
                            <ChevronDown className={`h-3 w-3 transition-transform ${expanded === w.id ? "rotate-180" : ""}`} strokeWidth={2.5} />
                          </button>
                        </td>
                      </tr>
                      {expanded === w.id && (
                        <tr data-testid={`worker-row-${idx}-details`} className="bg-slate-50">
                          <td colSpan={6} className="px-5 py-4">
                            {!detailsByWorker[w.id] && <div className="text-xs text-slate-500">Loading credentials…</div>}
                            {detailsByWorker[w.id] && detailsByWorker[w.id].length === 0 && (
                              <div className="text-xs text-slate-500">No credentials on file.</div>
                            )}
                            {detailsByWorker[w.id] && detailsByWorker[w.id].length > 0 && (
                              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                {detailsByWorker[w.id].map((c) => (
                                  <div key={c.id} className="border border-slate-200 bg-white p-3">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <div className="font-display text-sm font-bold text-[#0B1221]">{c.name}</div>
                                        <div className="text-[11px] text-slate-500">{c.issuer}</div>
                                      </div>
                                      <StatusBadge status={c.status} />
                                    </div>
                                    <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                                      {[
                                        ["Type", c.credential_type],
                                        ["Trade", c.trade],
                                        ["License #", c.license_number],
                                        ["State", c.state_jurisdiction],
                                        ["Issued", c.issue_date],
                                        ["Expires", c.expires],
                                        ["Verification", c.verification],
                                        ["Source", c.verification_source],
                                      ].map(([k, v]) => (
                                        <div key={k}>
                                          <dt className="font-mono text-[9px] uppercase tracking-[0.16em] text-slate-500">{k}</dt>
                                          <dd className="text-[11px] text-[#0B1221]">{v || <span className="text-slate-400">—</span>}</dd>
                                        </div>
                                      ))}
                                    </dl>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                    ))}
                    {filteredWorkers.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-500">
                          No workers match this filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Alerts side panel */}
          <aside className="lg:col-span-1">
            <div className="border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                    Alerts
                  </div>
                  <div className="font-display mt-0.5 text-lg font-bold text-[#0B1221]">
                    Immediate attention
                  </div>
                </div>
                <Bell className="h-4 w-4 text-[#FF5A1F]" strokeWidth={2.25} />
              </div>
              <ul className="divide-y divide-slate-100" data-testid="alerts-list">
                {data.alerts.map((a, idx) => (
                  <li
                    key={idx}
                    data-testid={`alert-${idx}`}
                    className={`border-l-[4px] ${severityStyle[a.severity] || severityStyle.low} px-4 py-3.5`}
                  >
                    <div className="text-sm font-semibold leading-snug">{a.title}</div>
                    {a.worker && (
                      <div className="mt-0.5 text-xs opacity-80">{a.worker}</div>
                    )}
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] opacity-70">
                      {a.severity === "high" ? "High priority" : a.severity === "medium" ? "Medium priority" : "Info"}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 border border-slate-200 bg-[#0B1221] p-5 text-white">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                Workforce readiness
              </div>
              <div className="font-display mt-1 text-base font-bold leading-snug">
                Know who&apos;s ready for the site.<br />
                Stop crew swaps at the gate.
              </div>
              <Link
                to="/wallet"
                data-testid="dashboard-cta-wallet"
                className="mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#FF5A1F] hover:underline"
              >
                See worker view <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
